/**
 * Curated bright UK-camping stock photos used as automatic fallbacks
 * whenever a campsite's primary photo is detected to be too dark/black
 * (broken upload, near-black image, decode error, etc.).
 *
 * Sources: Unsplash free licence — vetted to be vibrant, daylit, UK-ish.
 * Each campsite gets a CONSISTENT fallback (hashed by campsite_id),
 * so the same site always shows the same fallback image — no flicker
 * between page loads, and visitors won't notice the swap.
 */
export const BRIGHT_CAMP_FALLBACKS = [
  // Lake District-feel · tent at lakeside dawn
  "https://images.unsplash.com/photo-1504280390367-361c6d9f38f4?auto=format&fit=crop&w=1600&q=80",
  // Sunny meadow campervan
  "https://images.unsplash.com/photo-1532339142463-fd0a8979791a?auto=format&fit=crop&w=1600&q=80",
  // Highland glamping pods bright daylight
  "https://images.unsplash.com/photo-1499793983690-e29da59ef1c2?auto=format&fit=crop&w=1600&q=80",
  // Coastal tent at golden hour (bright not dark)
  "https://images.unsplash.com/photo-1537565266759-34bbc16be345?auto=format&fit=crop&w=1600&q=80",
  // Forest campsite with sunlight
  "https://images.unsplash.com/photo-1478131143081-80f7f84ca84d?auto=format&fit=crop&w=1600&q=80",
  // Bell tent yellow field
  "https://images.unsplash.com/photo-1499696010180-025ef6e1a8f9?auto=format&fit=crop&w=1600&q=80",
];

/** Stable cheap string hash → bucket index in [0, n). */
function stableHash(str) {
  let h = 0;
  for (let i = 0; i < str.length; i += 1) {
    h = ((h << 5) - h + str.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

/** Returns a deterministic bright fallback for this campsite. */
export function fallbackForCampsite(campsiteId) {
  const idx = stableHash(String(campsiteId || "default")) % BRIGHT_CAMP_FALLBACKS.length;
  return BRIGHT_CAMP_FALLBACKS[idx];
}

/**
 * Loads an image and decides whether it's too dark to display.
 * Returns a Promise<boolean> — true = too dark, swap it.
 *
 * Strategy: draw the image into a 16×16 offscreen canvas, sample average
 * luminance (Rec. 709), and report "too dark" if mean < 0.18 (≈ 46/255).
 * Threshold tuned so true-black & near-black images get caught, but
 * legitimate night-sky photos with a bit of moonlight still pass.
 *
 * Falls back to "not dark" on any error (CORS, decode fail, etc.) so we
 * never block on the check.
 */
export async function isImageTooDark(url) {
  if (!url || typeof url !== "string") return false;
  // SessionStorage cache so we never decode the same image twice per visit.
  const cacheKey = `cc_imgdark_${url}`;
  try {
    const cached = sessionStorage.getItem(cacheKey);
    if (cached === "dark") return true;
    if (cached === "ok") return false;
  } catch { /* ignore */ }

  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    let settled = false;
    const settle = (v) => {
      if (settled) return;
      settled = true;
      try { sessionStorage.setItem(cacheKey, v ? "dark" : "ok"); } catch { /* ignore */ }
      resolve(v);
    };
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = 16; canvas.height = 16;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, 16, 16);
        const { data } = ctx.getImageData(0, 0, 16, 16);
        let sum = 0;
        const px = data.length / 4;
        for (let i = 0; i < data.length; i += 4) {
          // Rec. 709 luma (perceptual brightness)
          const lum = 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
          sum += lum;
        }
        const mean = sum / px / 255; // 0..1
        settle(mean < 0.18);
      } catch {
        // CORS taint → can't read pixels. Assume OK rather than swap unnecessarily.
        settle(false);
      }
    };
    img.onerror = () => settle(true); // broken → treat as dark, fallback will swap.
    img.src = url;
    // Hard timeout — don't hang forever on slow images
    setTimeout(() => settle(false), 4000);
  });
}
